import React, {useEffect, useCallback} from 'react';
import {
    Modal, Row, Col, Button
} from 'react-bootstrap';
import {
    patchDrfRequest,
    getDrfRequest,
    buildUrl,
    postFormDataRequest,
    postDrfRequest,
} from "./components/utils";
import {
    RecordEditor,
    CreateRecord,
    CreateSchema,
    FileList,
    SchemaEditor,
    StackedSchemaIcon,
    PdfThumbnail,
    LoadingSpinner,
    ToastNotification,
    AddressManager,
    UserNameEditor,
    UserProfileHeader,
    Breadcrumbs,
    GlobalSettings
} from "./components/components"
import {XLg, PencilSquare, FileEarmarkTextFill, ChevronRight, GearFill, Download, Upload, PlusLg} from "react-bootstrap-icons";
import '../sass/project.scss';
import {createRoot} from 'react-dom/client';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';

const ViewContainer = ({ viewStack, onBack, onGoTo, pushSchemaPath, schemas, children }) => {
    return (
        <div className="view-container">
            <div className="view-header d-flex align-items-center mb-4 gap-3 mt-4">
                <Breadcrumbs stack={viewStack} onGoTo={onGoTo} pushSchemaPath={pushSchemaPath} schemas={schemas} />
            </div>
            <div className="view-content">
                {children}
            </div>
        </div>
    );
}

const SchemaRecords = ({
                         pk,
                         headline,
                         record_set,
                         schemafile_set,
                         json_schema,
                         default_from_address,
                         default_to_address,
                         default_data,
                         url,
                         refreshData,
                         showNotification,
                         pushView,
                         pushSchemaPath
                     }) => {
    const [showFiles, setShowFiles] = React.useState(false);
    const handleOpenSettings = () => {
        pushSchemaPath(pk, {
            type: 'schema',
            title: 'Einstellungen',
            props: {pk, headline, schema: json_schema, url, defaultFrom: default_from_address, defaultTo: default_to_address, defaultData: default_data, schemafile_set, refreshData, showNotification}
        });
    };

    return (
        <div className="schema-records-view">
            <div className="d-flex align-items-center mb-4 gap-3">
                <div className="d-flex align-items-center cursor-pointer gap-3" onClick={() => pushSchemaPath(pk)}>
                    <StackedSchemaIcon size={32} className="text-primary" />
                    <div className="d-flex flex-column">
                        <h2 className="mb-0">{headline}</h2>
                        <div className="text-muted small cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowFiles(!showFiles); }}>
                            {schemafile_set?.length || 0} {schemafile_set?.length === 1 ? 'Datei' : 'Dateien'}
                        </div>
                    </div>
                </div>
                <Button 
                    variant="primary" 
                    className="border-0 ms-2" 
                    title="Einstellungen"
                    onClick={handleOpenSettings}
                >
                    <GearFill size={24} />
                </Button>
            </div>

            {showFiles && (
                <div className="mb-4 p-3 bg-light rounded border">
                    <FileList 
                        file_set={schemafile_set} 
                        refreshData={refreshData}
                        show={showFiles}
                        onToggle={() => setShowFiles(!showFiles)}
                        showNotification={showNotification}
                        uploadUrl={buildUrl("/api/schemafiles/", {schema: pk})}
                        fullWidth={true}
                    />
                </div>
            )}

            <div className="d-flex flex-wrap gap-4 mb-4">
                <CreateRecord
                    headline={headline}
                    url={buildUrl("/api/records/")}
                    schema={pk}
                    refreshData={refreshData}
                    showNotification={showNotification}
                />
                {record_set.map((record) => (
                    <Record 
                        key={record.pk} 
                        {...record} 
                        pk={record.pk} 
                        json_schema={json_schema} 
                        default_from_address={default_from_address}
                        default_to_address={default_to_address}
                        default_data={default_data}
                        refreshData={refreshData} 
                        showNotification={showNotification} 
                        pushView={pushView}
                        pushSchemaPath={pushSchemaPath}
                    />
                ))}
            </div>
        </div>
    );
};

const App = ({ schemaUrl, addressUrl, userUrl, initialName, initialTelephone, initialEmail }) => {
    const [schemas, setSchemas] = React.useState([]);
    const [notification, setNotification] = React.useState({ show: false, message: '', variant: 'success' });
    const [viewStack, setViewStack] = React.useState([{ type: 'home', title: 'Vorlagen' }]);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const [hasRedirected, setHasRedirected] = React.useState(false);

    const pushView = useCallback((view) => setViewStack(prev => {
        const last = prev[prev.length - 1];
        if (last && last.type === view.type && last.props?.pk === view.props?.pk) {
            return prev;
        }
        return [...prev, view];
    }), []);

    const popView = useCallback(() => setViewStack(prev => prev.length > 1 ? prev.slice(0, -1) : prev), []);
    const goToView = useCallback((index) => setViewStack(prev => prev.slice(0, index + 1)), []);

    const showNotification = useCallback((message, variant = 'success') => {
        setNotification({ show: true, message, variant });
        setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
    }, []);

    useEffect(() => {
        const handleToggleSettings = () => {
            pushView({
                type: 'settings',
                title: 'Einstellungen',
                props: { schemaUrl, addressUrl, userUrl, initialName, initialTelephone, initialEmail }
            });
        };
        window.addEventListener('toggle-settings', handleToggleSettings);
        return () => window.removeEventListener('toggle-settings', handleToggleSettings);
    }, [pushView, schemaUrl, addressUrl, userUrl, initialName, initialTelephone, initialEmail]);

    const refreshData = useCallback(() => {
        getDrfRequest(buildUrl("/api/schemas/"), (data) => {
            // Pre-calculate depths for hierarchy visualization
            const depthMap = new Map();
            const getDepth = (pk) => {
                if (depthMap.has(pk)) return depthMap.get(pk);
                const s = data.find(x => x.pk === pk);
                if (!s || !s.parent) {
                    depthMap.set(pk, 0);
                    return 0;
                }
                const parentPk = (s.parent && typeof s.parent === 'object') ? s.parent.pk : s.parent;
                const d = 1 + getDepth(parentPk);
                depthMap.set(pk, d);
                return d;
            };
            const dataWithDepth = data.map(s => ({
                ...s,
                depth: getDepth(s.pk)
            }));
            setSchemas(dataWithDepth);
            setHasLoaded(true);
        }, (err) => {
            console.error(err);
            showNotification('Fehler beim Laden der Daten', 'danger');
        });
    }, [showNotification]);

    useEffect(() => {
        if (hasLoaded && schemas.length === 0 && !hasRedirected && viewStack.length === 1 && viewStack[0].type === 'home') {
            setHasRedirected(true);
            pushView({
                type: 'settings',
                title: 'Einstellungen',
                props: { schemaUrl, addressUrl, userUrl, initialName, initialTelephone, initialEmail }
            });
        }
    }, [hasLoaded, schemas, hasRedirected, viewStack, pushView, schemaUrl, addressUrl, userUrl, initialName, initialTelephone, initialEmail]);

    const pushSchemaPath = useCallback((target, andThenView = null) => {
        const path = [];
        let current = target;
        while (current) {
            const currentPk = (current && typeof current === 'object') ? current.pk : current;
            if (!currentPk) break;
            
            let schema = schemas.find(s => s.pk === currentPk);

            // If not found in state, but we have an object with the same PK, use it!
            if (!schema && typeof current === 'object' && current.pk === currentPk) {
                schema = current;
            }

            if (!schema) break;
            path.unshift(schema);
            current = schema.parent;
        }

        setViewStack(prev => {
            const newStack = [prev[0]]; // Home
            path.forEach(s => {
                const sParentPk = (s.parent && typeof s.parent === 'object') ? s.parent.pk : s.parent;
                const siblings = schemas.filter(sib => {
                    const sibParentPk = (sib.parent && typeof sib.parent === 'object') ? sib.parent.pk : sib.parent;
                    return sibParentPk === sParentPk;
                }).map(sib => ({pk: sib.pk, headline: sib.headline}));

                const cousins = schemas.filter(c => {
                    const cParentPk = (c.parent && typeof c.parent === 'object') ? c.parent.pk : c.parent;
                    return c.depth === s.depth && cParentPk !== sParentPk;
                }).map(c => ({pk: c.pk, headline: c.headline}));

                newStack.push({
                    type: 'schema_records',
                    title: s.headline,
                    siblings: siblings,
                    cousins: cousins,
                    props: {
                        pk: s.pk,
                        headline: s.headline,
                        record_set: s.record_set || [],
                        schemafile_set: s.schemafile_set || [],
                        json_schema: s.json_schema,
                        default_from_address: s.default_from_address,
                        default_to_address: s.default_to_address,
                        default_data: s.default_data,
                        url: s.url,
                        refreshData,
                        showNotification,
                        pushView,
                        pushSchemaPath
                    }
                });
            });
            if (andThenView) {
                // If it's a schema view, check if it's already the last one added to avoid duplicates
                const last = newStack[newStack.length - 1];
                if (andThenView.type === 'schema_records' && last && last.props?.pk === andThenView.props?.pk) {
                    // Skip if it's the same schema_records
                } else {
                    newStack.push(andThenView);
                }
            }
            return newStack;
        });
    }, [schemas, refreshData, showNotification, pushView]);

    const handleSchemaCreated = useCallback((e) => {
        const schema = e.detail;
        if (schema && schema.pk) {
            const schemaView = {
                type: 'schema',
                title: 'Einstellungen',
                props: {
                    pk: schema.pk,
                    headline: schema.headline,
                    schema: schema.json_schema,
                    url: schema.url,
                    defaultFrom: schema.default_from_address,
                    defaultTo: schema.default_to_address,
                    defaultData: schema.default_data,
                    schemafile_set: schema.schemafile_set,
                    refreshData,
                    showNotification
                }
            };
            pushSchemaPath(schema, schemaView);
        }
        refreshData();
    }, [pushSchemaPath, refreshData, showNotification]);

    const handleRecordCreated = useCallback((e) => {
        const record = e.detail;
        if (record && record.pk) {
            const recordView = {
                type: 'record',
                title: record.headline,
                props: {
                    pk: record.pk,
                    headline: record.headline,
                    recordfile_set: record.recordfile_set,
                    url: record.url,
                    schema: record.schema,
                    json_instance: record.json_instance,
                    json_schema: record.json_schema,
                    default_from_address: record.default_from_address,
                    default_to_address: record.default_to_address,
                    default_data: record.default_data,
                    refreshData,
                    showNotification
                }
            };
            pushSchemaPath(record.schema, recordView);
        }
        refreshData();
    }, [pushSchemaPath, refreshData, showNotification]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        window.addEventListener('schema-created', handleSchemaCreated);
        window.addEventListener('record-created', handleRecordCreated);

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !e.defaultPrevented && !document.body.classList.contains('modal-open')) {
                popView();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('schema-created', handleSchemaCreated);
            window.removeEventListener('record-created', handleRecordCreated);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleSchemaCreated, handleRecordCreated, popView]);

    const currentView = viewStack[viewStack.length - 1];

    let content;
    if (currentView.type === 'home') {
        content = (
            <div className="d-flex flex-wrap gap-4">
                {schemas.filter(s => !s.parent).map((schema) => (
                    <Schema 
                        key={schema.pk} 
                        {...schema} 
                        refreshData={refreshData} 
                        showNotification={showNotification}
                        pushView={pushView}
                        pushSchemaPath={pushSchemaPath}
                    />
                ))}
            </div>
        );
    } else if (currentView.type === 'schema') {
        const schemaData = schemas.find(s => s.pk === currentView.props.pk);
        content = (
            <SchemaEditor 
                {...currentView.props}
                {...(schemaData ? {
                    headline: schemaData.headline,
                    schema: schemaData.json_schema,
                    url: schemaData.url,
                    defaultFrom: schemaData.default_from_address,
                    defaultTo: schemaData.default_to_address,
                    defaultData: schemaData.default_data,
                    schemafile_set: schemaData.schemafile_set
                } : {})}
                refreshData={refreshData}
                showNotification={showNotification}
                onHide={popView}
            />
        );
    } else if (currentView.type === 'schema_records') {
        const schemaData = schemas.find(s => s.pk === currentView.props.pk);
        content = (
            <SchemaRecords
                {...currentView.props}
                {...(schemaData || {})}
                refreshData={refreshData}
                showNotification={showNotification}
                pushView={pushView}
                pushSchemaPath={pushSchemaPath}
                onHide={popView}
            />
        );
    } else if (currentView.type === 'record') {
        const schemaData = schemas.find(s => s.pk === currentView.props.schema);
        const recordData = schemaData?.record_set?.find(r => r.pk === currentView.props.pk);
        content = (
            <RecordEditor 
                {...currentView.props}
                {...(recordData || {})}
                refreshData={refreshData}
                showNotification={showNotification}
                onHide={popView}
            />
        );
    } else if (currentView.type === 'settings') {
        content = (
            <GlobalSettings 
                {...currentView.props}
                showNotification={showNotification}
                onHide={popView}
            />
        );
    }

    return (
        <div className="container-fluid overflow-visible">
            <LoadingSpinner />
            <ToastNotification 
                show={notification.show} 
                message={notification.message} 
                variant={notification.variant} 
                onHide={() => setNotification(prev => ({ ...prev, show: false }))} 
            />
            <ViewContainer viewStack={viewStack} onBack={popView} onGoTo={goToView} pushSchemaPath={pushSchemaPath} schemas={schemas}>
                {content}
            </ViewContainer>
        </div>
    );
}

const Record = ({pk, schema, headline, recordfile_set, url, json_instance, json_schema, default_from_address, default_to_address, default_data, refreshData, modified, showNotification, pushView, pushSchemaPath, is_building, intermediate_content, status}) => {
    const handleOpen = () => {
        pushSchemaPath(schema, {
            type: 'record',
            title: headline,
            props: {pk, schema, headline, recordfile_set, url, json_instance, json_schema, default_from_address, default_to_address, default_data, refreshData, showNotification, is_building, intermediate_content, status}
        });
    };

    const isDraft = status === 0;
    const statusColorClass = isDraft ? 'grey' : 'green';
    const statusBgClass = isDraft ? 'bg-' + statusColorClass : 'bg-' + statusColorClass;
    const statusTextClass = isDraft ? 'text-' + statusColorClass : 'text-' + statusColorClass;

    const pdfThumbnails = recordfile_set?.filter(f => f.thumbnail_url);
    const modifiedDate = modified ? new Date(modified).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }) : '';

    // Removed handleRecordClick - modal is now opened via designated editor icon

    if (pdfThumbnails?.length > 0) {
        const firstPdf = pdfThumbnails[0];
        return (
            <article className={`card border-record ${isDraft ? 'border-grey border-dotted border-2' : 'border-green'} bg-light h-100 overflow-visible cursor-pointer hover-border-record record-card`} 
                     onClick={handleOpen}>
                <div className="card-header bg-transparent border-0 pt-2 pb-2 px-3">
                    <div className="d-flex align-items-center mb-0">
                        <FileEarmarkTextFill size={32} className={`${statusTextClass} me-2`} />
                        <div className="d-flex flex-column overflow-hidden me-auto">
                            <h6 className={`card-title mb-0 text-truncate fw-normal ${statusBgClass} text-white px-2 py-1 rounded-1 d-inline-block`} style={{maxWidth: '100%'}}>
                                {headline}
                            </h6>
                            <div className="d-flex gap-2 text-muted mt-1" style={{fontSize: '0.65rem'}}>
                                <span>{modifiedDate}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body p-0 d-flex align-items-center justify-content-center bg-white" style={{minHeight: '100px'}}>
                    <PdfThumbnail 
                        thumbnailUrl={firstPdf.thumbnail_url} 
                        fileUrl={firstPdf.file} 
                        fullWidth={true} 
                        thumbnailWidth={400}
                    />
                </div>
            </article>
        );
    }

    return (
        <article className={`card border-record ${isDraft ? 'border-grey border-dotted border-2' : 'border-green'} bg-light h-100 overflow-visible cursor-pointer hover-border-record record-card`}
                 onClick={handleOpen}>
            <div className="card-header bg-transparent border-0 pt-2 pb-2 px-3">
                <div className="d-flex align-items-center mb-0">
                    <FileEarmarkTextFill size={32} className={`${statusTextClass} me-2`} />
                    <div className="d-flex flex-column overflow-hidden me-auto">
                        <h6 className={`card-title mb-0 text-truncate fw-normal ${statusBgClass} text-white px-2 py-1 rounded-1 d-inline-block`} style={{maxWidth: '100%'}}>
                            {headline}
                        </h6>
                        <div className="d-flex gap-2 text-muted mt-1" style={{fontSize: '0.65rem'}}>
                            <span>{modifiedDate}</span>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    )
}

const Schema = ({
                  headline,
                  schemafile_set,
                  record_set,
                  children,
                  pk,
                  url,
                  parent,
                  json_schema,
                  default_from_address,
                  default_to_address,
                  default_data,
                  refreshData,
                  created,
                  modified,
                  file_count,
                  record_count,
                  showNotification,
                  pushView,
                  pushSchemaPath
              }) => {
    const handleOpenRecords = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        pushSchemaPath(pk);
    };

    const handleOpenSettings = (e) => {
        e.stopPropagation();
        pushSchemaPath(pk, {
            type: 'schema',
            title: 'Einstellungen',
            props: {pk, headline, schema: json_schema, url, defaultFrom: default_from_address, defaultTo: default_to_address, defaultData: default_data, schemafile_set, refreshData, showNotification}
        });
    };

    const handleCreateRecord = (e) => {
        e.stopPropagation();
        const createUrl = buildUrl("/api/records/");
        const data = {
            "schema": pk,
        }
        postDrfRequest(createUrl, data, (response) => {
            if (showNotification) showNotification('Datensatz erfolgreich erstellt');
            if (refreshData) refreshData();
            window.dispatchEvent(new CustomEvent('record-created', { detail: response }));
        }, (err) => {
            if (showNotification) showNotification('Fehler beim Erstellen des Datensatzes', 'danger');
        })
    };

    const modifiedDate = modified ? new Date(modified).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }) : '';

    const isSubSchema = !!parent;

    return (
        <article className={`card ${isSubSchema ? 'border bg-light-subtle' : 'border-schema hover-border-schema border-0 schema-card'} overflow-visible cursor-pointer`} onClick={handleOpenRecords}>
            <div className={`card-header ${isSubSchema ? 'bg-transparent' : 'bg-white'} border-0 d-flex justify-content-between align-items-start`}>
                <div className="d-flex align-items-center overflow-hidden">
                    <StackedSchemaIcon size={32} className="text-primary me-2 flex-shrink-0" />
                    <div className="d-flex flex-column overflow-hidden">
                        <h5 className="card-title mb-0 text-truncate fw-normal bg-primary text-white px-2 py-1 rounded-1 d-inline-block" style={{maxWidth: '100%'}}>
                            {headline}
                        </h5>
                        <div className="d-flex gap-2 text-muted mt-1" style={{fontSize: '0.65rem'}}>
                            <span>{modifiedDate}</span>
                            <span>{record_count} {record_count === 1 ? 'Datensatz' : 'Datensätze'}</span>
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <Button className="btn-orange border-0 ms-2 btn btn-primary" onClick={handleCreateRecord} title="Neuer Datensatz">
                        <PlusLg size={20} />
                    </Button>
                    <Button variant="primary" className="border-0 ms-2" onClick={handleOpenSettings} title="Einstellungen">
                        <GearFill size={20} />
                    </Button>
                </div>
            </div>
            <div className="card-body p-2">
                {children && children.length > 0 && (
                    <div className="mt-2 pt-4 border-top">
                        <small className="text-uppercase fw-bold text-muted mb-3 d-block"
                               style={{fontSize: '0.7rem', letterSpacing: '0.05em'}}>Unter-Vorlagen von {headline}</small>
                        <div className="d-flex flex-column gap-3 ms-3 ps-3">
                            {children.map((child) => (
                                <Schema 
                                    key={child.pk} 
                                    {...child} 
                                    refreshData={refreshData} 
                                    showNotification={showNotification} 
                                    pushView={pushView}
                                    pushSchemaPath={pushSchemaPath}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
};

window.renderApp = () => {
    const domNode = document.getElementById('app');
    const headerNode = document.getElementById('header-app');
    const schemaUrl = headerNode?.getAttribute('data-schema-url') || buildUrl("/api/schemas/");
    const addressUrl = headerNode?.getAttribute('data-address-url') || buildUrl("/api/addresses/");
    const userUrl = headerNode?.getAttribute('data-url') || '';
    const initialName = headerNode?.getAttribute('data-initial-name') || '';
    const initialTelephone = headerNode?.getAttribute('data-initial-telephone') || '';
    const initialEmail = headerNode?.getAttribute('data-initial-email') || '';

    if (domNode) createRoot(domNode).render(
        <App 
            schemaUrl={schemaUrl} 
            addressUrl={addressUrl} 
            userUrl={userUrl}
            initialName={initialName}
            initialTelephone={initialTelephone}
            initialEmail={initialEmail}
        />
    );

    const addressNode = document.getElementById('address-manager');
    if (addressNode) {
        const root = createRoot(addressNode);
        const addrManagerUrl = buildUrl("/api/addresses/");
        const addressUserName = addressNode.getAttribute('data-user-name') || '';
        
        const AddressApp = () => {
            const [notification, setNotification] = React.useState({ show: false, message: '', variant: 'success' });
            const showNotification = (message, variant = 'success') => {
                setNotification({ show: true, message, variant });
                setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
            };

            return (
                <>
                    <ToastNotification 
                        show={notification.show} 
                        message={notification.message} 
                        variant={notification.variant} 
                        onHide={() => setNotification(prev => ({ ...prev, show: false }))} 
                    />
                    <AddressManager url={addrManagerUrl} showNotification={showNotification} userName={addressUserName} />
                </>
            );
        };
        root.render(<AddressApp />);
    }

    const userNode = document.getElementById('user-name-editor');
    if (userNode) {
        const root = createRoot(userNode);
        const userName = userNode.getAttribute('data-initial-name') || '';
        const userEditorUrl = userNode.getAttribute('data-url') || '';

        const UserApp = () => {
            const [notification, setNotification] = React.useState({ show: false, message: '', variant: 'success' });
            const showNotification = (message, variant = 'success') => {
                setNotification({ show: true, message, variant });
                setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
            };

            return (
                <>
                    <ToastNotification 
                        show={notification.show} 
                        message={notification.message} 
                        variant={notification.variant} 
                        onHide={() => setNotification(prev => ({ ...prev, show: false }))} 
                    />
                    <UserNameEditor initialName={userName} url={userEditorUrl} showNotification={showNotification} />
                </>
            );
        };
        root.render(<UserApp />);
    }

    if (headerNode) {
        const root = createRoot(headerNode);

        const HeaderApp = () => {
            const [notification, setNotification] = React.useState({ show: false, message: '', variant: 'success' });

            const showNotification = (message, variant = 'success') => {
                setNotification({ show: true, message, variant });
                setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
            };

            return (
                <div className="d-flex align-items-center gap-3">
                    <ToastNotification 
                        show={notification.show} 
                        message={notification.message} 
                        variant={notification.variant} 
                        onHide={() => setNotification(prev => ({ ...prev, show: false }))} 
                    />
                    <UserProfileHeader
                        initialName={initialName} 
                        initialTelephone={initialTelephone} 
                        initialEmail={initialEmail} 
                        onClick={() => window.dispatchEvent(new CustomEvent('toggle-settings'))} 
                    />
                    <Button 
                        variant="link" 
                        className="p-2 text-secondary shadow-none" 
                        onClick={() => window.dispatchEvent(new CustomEvent('toggle-settings'))} 
                        title="Einstellungen"
                    >
                        <GearFill size={32} />
                    </Button>
                </div>
            );
        };
        root.render(<HeaderApp />);
    }
}
